import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './FumeHoodCertificationLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  numberOfHoods: string
}

function FumeHoodCertificationLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          numberOfHoods: data.numberOfHoods,
          _subject: 'Fume Hood Testing & Certification Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fume Hood Testing & Certification',
    description:
      'ASHRAE 110 face velocity testing and annual certification for laboratory fume hoods, with documented pass/fail results and remediation guidance.',
    provider: { '@type': 'Organization', name: 'Laboratory Specialists' },
    serviceType: 'Fume Hood Performance Testing',
  }

  return (
    <div className="fhc-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero */}
      <section className="fhc-hero">
        <div className="fhc-hero-bg" aria-hidden />
        <div className="container fhc-hero-inner">
          <p className="fhc-hero-eyebrow">ASHRAE 110 Testing · Annual Certification · Compliance Documentation</p>
          <h1 className="fhc-hero-title">
            Is Your Fume Hood Actually Containing Fumes?
          </h1>
          <p className="fhc-hero-subtitle">
            We test every fume hood against ASHRAE 110 face velocity and containment standards, then certify the
            results—so you know your hoods protect your team, not just look like they do.
          </p>
          <div className="fhc-hero-cta">
            <button type="button" className="fhc-cta-button" onClick={scrollToForm}>
              Schedule Fume Hood Testing
            </button>
            <p className="fhc-hero-cta-note">Response within 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Risk / urgency */}
      <section className="fhc-risk">
        <div className="container">
          <h2 className="fhc-section-title">The Cost of Skipping Fume Hood Testing</h2>
          <p className="fhc-section-intro">
            A fume hood can look fine and still fail to contain hazardous vapors. Face velocity and containment
            only get confirmed by testing—not by a visual check.
          </p>
          <div className="fhc-risk-grid">
            <div className="fhc-risk-card">
              <div className="fhc-risk-icon" aria-hidden>⚠️</div>
              <h3>Chemical Exposure Liability</h3>
              <p>
                A hood operating below rated face velocity exposes researchers to hazardous vapors. ASHRAE 110
                sets the performance benchmark specifically to catch this before someone gets hurt.
              </p>
            </div>
            <div className="fhc-risk-card">
              <div className="fhc-risk-icon" aria-hidden>🔍</div>
              <h3>Failed Inspections & Fines</h3>
              <p>
                OSHA, state fire marshals, and institutional EHS departments expect current test certificates.
                Missing or expired documentation can trigger citations or a lab shutdown.
              </p>
            </div>
            <div className="fhc-risk-card">
              <div className="fhc-risk-icon" aria-hidden>🕰️</div>
              <h3>Silent Degradation</h3>
              <p>
                Belts, filters, baffles, and sash seals wear out gradually. A hood can drift out of spec for
                months before anyone notices without a scheduled test.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="fhc-scope">
        <div className="container">
          <h2 className="fhc-section-title">What Our Fume Hood Testing Includes</h2>

          <div className="fhc-scope-grid">
            <div className="fhc-scope-block">
              <h3>ASHRAE 110 Performance Testing</h3>
              <ul>
                <li>Face velocity measurement and uniformity across the sash opening</li>
                <li>Sash movement, tracking, and containment verification</li>
                <li>Smoke/tracer visualization to confirm capture and containment</li>
                <li>Assessment of both ducted and ductless hood configurations</li>
                <li>Fan belt condition and filter status (ductless systems)</li>
              </ul>
            </div>

            <div className="fhc-scope-block">
              <h3>Certification & Documentation</h3>
              <ul>
                <li>Written pass/fail certificate for each hood, dated and signed</li>
                <li>Certification label affixed to the hood for inspectors</li>
                <li>Digital records for your EHS and compliance files</li>
                <li>Prioritized notes on any hood that fails or is near threshold</li>
              </ul>
            </div>

            <div className="fhc-scope-block">
              <h3>Recommended Testing Schedule</h3>
              <ul>
                <li>Annual testing—NFPA 45 mandates this minimum cadence for lab hoods</li>
                <li>Retesting after a hood is moved, serviced, or ductwork is altered</li>
                <li>Testing before new equipment or chemicals change airflow needs</li>
                <li>Immediate retest for any hood flagged in a prior inspection</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ASHRAE 110 callout */}
      <section className="fhc-standard">
        <div className="container">
          <div className="fhc-standard-card">
            <h2>What Is ASHRAE 110 Testing?</h2>
            <p>
              ASHRAE 110, <em>Method of Testing Performance of Laboratory Fume Hoods</em>, is the standard testing
              protocol used to verify that a fume hood actually contains the vapors it's designed to capture. It
              measures:
            </p>
            <ul>
              <li>Face velocity at the sash opening, checked for adequacy and uniformity</li>
              <li>Containment performance using a tracer gas challenge test</li>
              <li>Sash operation and its effect on airflow and containment</li>
            </ul>
            <p>
              A hood can appear to be running normally and still fail an ASHRAE 110 test. Institutional EHS
              programs, OSHA, and most state fire codes expect documented, current test results—not just a visual
              inspection.
            </p>
            <p>
              The annual cadence itself comes from a separate standard: NFPA 45,{' '}
              <em>Standard on Fire Protection for Laboratories Using Chemicals</em>, mandates that laboratory hoods
              and their exhaust systems be inspected and tested when installed or modified, and at least annually
              thereafter. ASHRAE 110 is the test method we use to meet that requirement.
            </p>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="fhc-audience">
        <div className="container">
          <h2 className="fhc-section-title">Who Needs Fume Hood Testing</h2>
          <ul className="fhc-audience-list">
            <li>University and college labs due for annual fume hood certification</li>
            <li>Pharmaceutical and biotech facilities preparing for a regulatory audit</li>
            <li>Labs that have added new equipment or chemicals since the last test</li>
            <li>Facilities with hoods that were moved, serviced, or reducted recently</li>
            <li>Any lab whose fume hoods haven't been tested in the last 12 months</li>
            <li>Facilities managers standardizing testing across multiple lab buildings</li>
          </ul>
        </div>
      </section>

      {/* Objections */}
      <section className="fhc-objections">
        <div className="container">
          <ul className="fhc-bullets">
            <li>
              <strong>Hood seems to be working fine?</strong> Visual inspection can't measure face velocity or
              containment—only ASHRAE 110 testing confirms actual performance.
            </li>
            <li>
              <strong>Not sure how often you need testing?</strong> We'll tell you honestly based on hood age,
              usage, and your institution's compliance requirements.
            </li>
            <li>
              <strong>Managing hoods across multiple labs?</strong> We can test and certify an entire building or
              campus on one coordinated schedule.
            </li>
          </ul>
        </div>
      </section>

      {/* Form */}
      <section className="fhc-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="fhc-form-card">
            <h2 className="fhc-form-title">Schedule Fume Hood Testing</h2>
            <p className="fhc-form-lead-magnet">
              Tell us about your hoods and we'll follow up within 24 hours with next steps and pricing—no
              obligation.
            </p>

            {isSubmitted ? (
              <div className="fhc-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll reach out to schedule your fume hood testing and certification.</p>
              </div>
            ) : (
              <form className="fhc-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="fhc-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}

                <div className="fhc-form-group">
                  <label htmlFor="fhc-name">
                    Full Name <span className="fhc-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="fhc-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="fhc-error" role="alert">{errors.name.message}</span>
                  )}
                </div>

                <div className="fhc-form-group">
                  <label htmlFor="fhc-email">
                    Email <span className="fhc-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="fhc-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="fhc-error" role="alert">{errors.email.message}</span>
                  )}
                </div>

                <div className="fhc-form-group">
                  <label htmlFor="fhc-phone">
                    Phone <span className="fhc-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="fhc-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="fhc-error" role="alert">{errors.phone.message}</span>
                  )}
                </div>

                <div className="fhc-form-group">
                  <label htmlFor="fhc-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="fhc-company"
                    {...register('company')}
                    placeholder="Your lab or organization"
                  />
                </div>

                <div className="fhc-form-group">
                  <label htmlFor="fhc-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="fhc-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="fhc-form-group">
                  <label htmlFor="fhc-numberOfHoods">
                    How many fume hoods need testing? <span className="fhc-required">*</span>
                  </label>
                  <select
                    id="fhc-numberOfHoods"
                    {...register('numberOfHoods', { required: 'Please select an option' })}
                    aria-invalid={errors.numberOfHoods ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="1">1 hood</option>
                    <option value="2-5">2–5 hoods</option>
                    <option value="6-15">6–15 hoods</option>
                    <option value="16+">16+ hoods</option>
                    <option value="not-sure">Not sure yet</option>
                  </select>
                  {errors.numberOfHoods && (
                    <span className="fhc-error" role="alert">{errors.numberOfHoods.message}</span>
                  )}
                </div>

                <p className="fhc-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="fhc-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Schedule My Testing'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="fhc-legal">
        <div className="container">
          <p className="fhc-legal-text">
            Laboratory Specialists. Fume hood testing and certification services. ASHRAE 110 and NFPA 45 references
            are for informational purposes; always consult your AHJ for jurisdiction-specific requirements.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default FumeHoodCertificationLandingPage
